import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card, Spin, Alert, Button, Tooltip } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, FullscreenOutlined, LeftOutlined, RightOutlined, ColumnHeightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useTheme } from '../../contexts/useTheme';
import type { TimelineProps } from './types';
import { TimelineTitle } from './TimelineTitle';
import { TimelineLegend } from './TimelineLegend';
import { SessionTooltip, DataBarTooltip } from './TimelineTooltip';
import { useTimelineData } from './hooks/useTimelineData';
import { useTimelineCanvas } from './hooks/useTimelineCanvas';
import { useTimelineInteraction } from './hooks/useTimelineInteraction';
import { useTimelineViewport } from './hooks/useTimelineViewport';
import './Timeline.css';

dayjs.extend(utc);
dayjs.extend(timezone);

export const Timeline: React.FC<TimelineProps> = ({ 
  onDateRangeChange, 
  onDatePointChange, 
  stationsData, 
  forecastAvailability, 
  radarActivityAvailability, 
  stationActivityAvailability, 
  mode = 'range',
  liveStartMinutes = -5,
  liveEndMinutes = 60,
  onLiveStartMinutesChange,
  onLiveEndMinutesChange,
  mapTileProvider = 'radar',
  liveEndMinutesForRadar = 30, // Default 30 for nowcasting; Observations passes 0
  onLiveEndMinutesForRadarChange,
  refreshing = false,
  onRefresh,
  liveStartMinLimit = -60,
  onLiveModeChange,
  clampSelectionToNow = false,
  compact = false,
  onCompactChange,
}) => {
  const { selectedEvent } = useEventSelection();
  const { theme } = useTheme();

  const {
    sessions,
    loading,
    error,
    selectedRange,
    setSelectedRange,
    selectedPoint,
    setSelectedPoint,
    isLiveMode,
    setIsLiveMode,
    currentTime,
    pendingStartMinutes,
    setPendingStartMinutes,
    pendingEndMinutes,
    setPendingEndMinutes,
    canvasRenderKey,
    setCanvasRenderKey,
  } = useTimelineData({
    stationsData,
    mode,
    liveStartMinutes,
    liveEndMinutes,
    liveEndMinutesForRadar,
    mapTileProvider,
    liveStartMinLimit,
    onDateRangeChange,
    onDatePointChange,
    onLiveModeChange,
  });

  const timelineRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Legend collapse/expand state
  const [legendExpanded, setLegendExpanded] = useState(true);

  // Effect to re-draw canvas when container size changes (e.g. window resize)
  useEffect(() => {
    const container = timelineRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      setCanvasRenderKey((prev) => prev + 1);
    });
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [loading, error, setCanvasRenderKey]);

  // Get event time bounds (full week) - use date strings to avoid timezone issues
  const getEventBounds = useCallback(() => {
    if (!selectedEvent) return null;
    
    const tz = selectedEvent.circuit?.time_zone || 'UTC';
    
    // Parse the event's calendar dates IN THE EVENT'S TIMEZONE
    const startDate = dayjs.tz(selectedEvent.start_date, tz).startOf('day').toDate();
    const endDate = dayjs.tz(selectedEvent.end_date, tz).endOf('day').toDate();
    
    return { start: startDate, end: endDate };
  }, [selectedEvent]);

  const eventKey = selectedEvent
    ? `${selectedEvent.season_year}-${selectedEvent.circuit_id}-${selectedEvent.start_date}`
    : '';
  const {
    viewBounds,
    zoomIn,
    zoomOut,
    panLeft,
    panRight,
    resetView,
    setViewBounds,
    canZoomIn,
    canZoomOut,
    canPanLeft,
    canPanRight,
    isZoomed,
  } = useTimelineViewport({
    getEventBounds,
    eventKey,
  });

  const getViewBounds = useCallback(() => {
    return viewBounds ?? getEventBounds();
  }, [viewBounds, getEventBounds]);

  useTimelineCanvas({
    canvasRef,
    selectedEvent,
    sessions,
    forecastAvailability,
    radarActivityAvailability,
    stationActivityAvailability,
    selectedRange,
    selectedPoint,
    theme,
    mode,
    currentTime,
    canvasRenderKey,
    viewBounds,
    getEventBounds,
    eventTimezone: selectedEvent?.circuit?.time_zone || 'UTC',
    compact,
  });

  const {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    adjustStartTime,
    adjustEndTime,
    isDragging,
    isDraggingRange,
    isPanning,
    isDraggingEdge,
    isHoveringRange,
    isHoveringEdge,
    hoveredSession,
    hoveredDataBar,
    tooltipPosition,
    hoverTime,
  } = useTimelineInteraction({
    canvasRef,
    sessions,
    selectedRange,
    selectedPoint,
    setSelectedRange,
    setSelectedPoint,
    setIsLiveMode,
    mode,
    forecastAvailability,
    radarActivityAvailability,
    stationActivityAvailability,
    getViewBounds,
    getEventBounds,
    setViewBounds,
    onDateRangeChange,
    onDatePointChange,
    clampSelectionToNow,
    compact,
  });

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const centerRatio = (e.clientX - rect.left) / rect.width;
      if (e.deltaY < 0) {
        zoomIn(centerRatio);
      } else if (e.deltaY > 0) {
        zoomOut(centerRatio);
      }
      e.preventDefault();
    },
    [zoomIn, zoomOut]
  );

  useEffect(() => {
    const container = timelineRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  if (!selectedEvent) {
    return (
      <Card>
        <Alert title="No Event Selected" description="Please select an event to view timeline." type="info" showIcon />
      </Card>
    );
  }

  return (
    <Card 
      styles={{ body: { padding: compact ? '2px 8px' : '8px 12px' } }}
      size={compact ? 'small' : undefined}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '6px' : '12px' }}>
          <TimelineTitle
            selectedEvent={selectedEvent}
            theme={theme}
            mode={mode}
            selectedRange={selectedRange}
            selectedPoint={selectedPoint}
            setSelectedRange={setSelectedRange}
            setSelectedPoint={setSelectedPoint}
            isLiveMode={isLiveMode}
            setIsLiveMode={setIsLiveMode}
            pendingStartMinutes={pendingStartMinutes}
            setPendingStartMinutes={setPendingStartMinutes}
            pendingEndMinutes={pendingEndMinutes}
            setPendingEndMinutes={setPendingEndMinutes}
            adjustStartTime={adjustStartTime}
            adjustEndTime={adjustEndTime}
            liveStartMinLimit={liveStartMinLimit}
            liveStartMinutes={liveStartMinutes}
            liveEndMinutes={liveEndMinutes}
            liveEndMinutesForRadar={liveEndMinutesForRadar}
            mapTileProvider={mapTileProvider}
            onLiveStartMinutesChange={onLiveStartMinutesChange}
            onLiveEndMinutesChange={onLiveEndMinutesChange}
            onLiveEndMinutesForRadarChange={onLiveEndMinutesForRadarChange}
            onDateRangeChange={onDateRangeChange}
            onDatePointChange={onDatePointChange}
            onRefresh={onRefresh}
            refreshing={refreshing}
            compact={compact}
          />
          {hoverTime && (
            <span
              style={{
                fontSize: compact ? '10px' : '12px',
                fontWeight: 500,
                fontFamily: 'monospace',
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                padding: compact ? '1px 6px' : '3px 10px',
                borderRadius: '4px',
                whiteSpace: 'nowrap',
                border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
              }}
            >
              {dayjs(hoverTime).tz(selectedEvent?.circuit?.time_zone || 'UTC').format('MMM D, HH:mm')}
            </span>
          )}
        </div>
      }
      style={{ marginBottom: 0 }}
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '4px' : '8px', overflow: 'hidden' }}>
          <TimelineLegend
            theme={theme}
            legendExpanded={legendExpanded}
            onLegendExpandedChange={(expanded) => setLegendExpanded(expanded)}
            forecastAvailability={forecastAvailability}
            radarActivityAvailability={radarActivityAvailability}
            stationActivityAvailability={stationActivityAvailability}
            compact={compact}
          />
          {onCompactChange && (
            <>
              <div style={{ width: 1, height: compact ? 14 : 18, backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)' }} />
              <Tooltip title={compact ? 'Expand timeline' : 'Compact timeline'}>
                <Button
                  type="text"
                  size="small"
                  icon={<ColumnHeightOutlined />}
                  onClick={() => onCompactChange(!compact)}
                  aria-label={compact ? 'Expand timeline' : 'Compact timeline'}
                  style={compact ? { width: 20, height: 20, minWidth: 20, padding: 0, fontSize: 11, opacity: 0.7 } : { opacity: 0.7 }}
                />
              </Tooltip>
            </>
          )}
          <div style={{ width: 1, height: compact ? 14 : 18, backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)' }} />
          <div className={`timeline-zoom-controls${compact ? ' timeline-zoom-controls-compact' : ''}`}>
            <Tooltip title="Pan left (or Shift + drag)">
              <Button
                type="text"
                size="small"
                icon={<LeftOutlined />}
                onClick={panLeft}
                disabled={!canPanLeft}
                aria-label="Pan left"
              />
            </Tooltip>
            <Tooltip title="Pan right (or Shift + drag)">
              <Button
                type="text"
                size="small"
                icon={<RightOutlined />}
                onClick={panRight}
                disabled={!canPanRight}
                aria-label="Pan right"
              />
            </Tooltip>
            <Tooltip title="Zoom in">
              <Button
                type="text"
                size="small"
                icon={<ZoomInOutlined />}
                onClick={() => zoomIn(0.5)}
                disabled={!canZoomIn}
                aria-label="Zoom in"
              />
            </Tooltip>
            <Tooltip title="Zoom out" placement="bottomRight">
              <Button
                type="text"
                size="small"
                icon={<ZoomOutOutlined />}
                onClick={() => zoomOut(0.5)}
                disabled={!canZoomOut}
                aria-label="Zoom out"
              />
            </Tooltip>
            <Tooltip title="Reset zoom" placement="bottomRight">
              <span
                role="button"
                tabIndex={0}
                onClick={isZoomed ? resetView : undefined}
                onKeyDown={(e) => { if (e.key === 'Enter' && isZoomed) resetView(); }}
                aria-label="Reset zoom"
                aria-disabled={!isZoomed}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: compact ? 20 : 24,
                  height: compact ? 20 : 24,
                  minWidth: compact ? 20 : 24,
                  cursor: isZoomed ? 'pointer' : 'not-allowed',
                  opacity: isZoomed ? 1 : 0.25,
                  borderRadius: 4,
                  fontSize: compact ? 11 : 14,
                  color: 'inherit',
                }}
              >
                <FullscreenOutlined />
              </span>
            </Tooltip>
          </div>
        </div>
      }
    >
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: compact ? '36px' : '60px',
          width: '100%'
        }}>
          <Spin size={compact ? 'default' : 'large'} />
        </div>
      ) : error ? (
        <Alert title="Error" description={error} type="error" showIcon />
      ) : (
        <div 
          ref={timelineRef}
          style={{ 
            width: '100%', 
            height: compact ? '36px' : '60px', 
            position: 'relative',
            cursor: isPanning
              ? 'grabbing'
              : mode === 'point' 
                ? 'pointer' 
                : isDraggingEdge || isHoveringEdge
                  ? 'ew-resize'
                  : isDraggingRange 
                    ? 'grabbing' 
                    : isHoveringRange 
                      ? 'grab' 
                      : isDragging 
                        ? 'col-resize' 
                        : 'crosshair'
          }}
          role="region"
          aria-label="Timeline"
        >
          <canvas
            ref={canvasRef}
            style={{ 
              width: '100%', 
              height: '100%',
              display: 'block',
              cursor: isPanning
                ? 'grabbing'
                : mode === 'point' 
                  ? 'pointer' 
                  : isDraggingEdge || isHoveringEdge
                    ? 'ew-resize'
                    : isDraggingRange 
                      ? 'grabbing' 
                      : isHoveringRange 
                        ? 'grab' 
                        : isDragging 
                          ? 'col-resize' 
                          : 'crosshair'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          />
          
          {hoveredSession && tooltipPosition && (
            <SessionTooltip
              session={hoveredSession}
              position={tooltipPosition}
              theme={theme}
              timezone={selectedEvent?.circuit?.time_zone || 'UTC'}
            />
          )}
          {hoveredDataBar && tooltipPosition && !hoveredSession && (
            <DataBarTooltip
              dataBar={hoveredDataBar}
              position={tooltipPosition}
              theme={theme}
              timezone={selectedEvent?.circuit?.time_zone || 'UTC'}
            />
          )}
        </div>
      )}
    </Card>
  );
};
